import { useState, useMemo } from "react";
import * as d3 from "d3";
import { useApiUrl, useCustom, useList } from "@refinedev/core";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  CloseButton,
  Flex,
  Grid,
  Group,
  Loader,
  Pagination,
  Paper,
  Progress,
  ScrollArea,
  Select,
  Table,
  Tabs,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { Link, useNavigate } from "react-router-dom";
import { DatePicker } from "@mantine/dates";
import {
  IconChartBar,
  IconDownload,
  IconFileAnalytics,
  IconPercentage,
  IconRefresh,
} from "@tabler/icons-react";
import { selectBrands } from "entities/cancel_flow/brand";
import { colorsByOutcome } from "entities/cancel_flow/outcome";
import type { CancelPerson, OfferInteraction } from "entities/cancel_flow";

const REASON_COLORS = [
  "#ff6b6b", "#fcc419", "#51cf66", "#339af0", "#66d9c8",
  "#343a40", "#e64980", "#ff922b", "#3b5bdb", "#c0eb75",
  "#845ef7", "#20c997", "#fd7e14", "#868e96", "#f06595",
];

type SummaryData = {
  total: number;
  cancelled: number;
  deflected: number;
  abandoned: number;
  in_progress: number;
  deflection_rate: number;
  date_from: string;
  date_to: string;
};

type ReasonBreakdownItem = {
  reason: string;
  count: number;
};

type OfferBreakdownItem = {
  offer_type: string;
  offer_value: string;
  shown: number;
  accepted: number;
  acceptance_rate: number;
  saved: number;
  lost: number;
  watching: number;
  save_rate: number;
  saved_revenue: number;
};

type OffersBreakdownData = {
  total_shown: number;
  total_accepted: number;
  acceptance_rate: number;
  by_type: {
    offer_type: string;
    shown: number;
    accepted: number;
  }[];
  by_offer: OfferBreakdownItem[];
};

function formatOfferName(offer_type: string, offer_value: string): string {
  if (offer_type === "pause") return "Skip A Month";
  return `${offer_value} Discount`;
}

export const CancelFlowReport = () => {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const [brand, setBrand] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [dateTo, setDateTo] = useState<Date | null>(new Date());
  const [activeTab, setActiveTab] = useState<string | null>("cancels");
  const [selectedOffer, setSelectedOffer] = useState<{
    offer_type: string;
    offer_value: string;
  } | null>(null);
  const [detailPage, setDetailPage] = useState(1);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [reasonDetailPage, setReasonDetailPage] = useState(1);
  const [hoveredReasonIndex, setHoveredReasonIndex] = useState<number | null>(null);

  const endOfDay = (d: Date) => {
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const queryParams: Record<string, string> = {};
  if (brand) queryParams.brand = brand;
  if (dateFrom) queryParams.date_from = dateFrom.toISOString();
  if (dateTo) queryParams.date_to = endOfDay(dateTo).toISOString();

  const { data: summaryData, isLoading: summaryLoading, refetch: refetchSummary } =
    useCustom<SummaryData>({
      url: `${apiUrl}/cancel_dashboard/summary`,
      method: "get",
      config: { query: queryParams },
    });

  const { data: reasonsData, isLoading: reasonsLoading, refetch: refetchReasons } = useCustom<
    ReasonBreakdownItem[]
  >({
    url: `${apiUrl}/cancel_dashboard/reasons_breakdown`,
    method: "get",
    config: { query: queryParams },
  });

  const { data: offersData, isLoading: offersLoading, refetch: refetchOffers } =
    useCustom<OffersBreakdownData>({
      url: `${apiUrl}/cancel_dashboard/offers_breakdown`,
      method: "get",
      config: { query: queryParams },
    });

  const { data: peopleData, isLoading: peopleLoading } = useList<CancelPerson>({
    resource: "cancel_people",
    filters: [
      { field: "tab", operator: "eq", value: "all" },
      ...(brand ? [{ field: "brand", operator: "eq" as const, value: brand }] : []),
      ...(dateFrom ? [{ field: "date_from", operator: "eq" as const, value: dateFrom.toISOString() }] : []),
      ...(dateTo ? [{ field: "date_to", operator: "eq" as const, value: endOfDay(dateTo).toISOString() }] : []),
    ],
    sorters: [{ field: "started_at", order: "desc" }],
    pagination: { pageSize: 1000 },
    queryOptions: { enabled: !!selectedOffer || activeTab === "reasons" },
  });

  const offerDetails = useMemo(() => {
    if (!selectedOffer || !peopleData?.data) return [];
    return peopleData.data
      .map((person) => {
        const interaction = person.offer_interactions.find(
          (i) =>
            i.offer_type === selectedOffer.offer_type &&
            i.offer_value === selectedOffer.offer_value
        );
        if (!interaction) return null;
        return { person, interaction };
      })
      .filter(Boolean) as { person: CancelPerson; interaction: OfferInteraction }[];
  }, [selectedOffer, peopleData]);

  const reasonStats = useMemo(() => {
    const people = peopleData?.data ?? [];
    const grouped: Record<string, { visits: number; cancels: number; savesAndDeflects: number }> = {};
    for (const person of people) {
      const label = person.cancel_survey_response?.cancellation_reason?.label ?? "No reason given";
      if (!grouped[label]) grouped[label] = { visits: 0, cancels: 0, savesAndDeflects: 0 };
      grouped[label].visits += 1;
      if (person.outcome === "cancelled") grouped[label].cancels += 1;
      if (person.outcome === "deflected") grouped[label].savesAndDeflects += 1;
    }
    const totals = { visits: 0, cancels: 0, savesAndDeflects: 0 };
    const rows = Object.entries(grouped)
      .map(([reason, stats]) => {
        totals.visits += stats.visits;
        totals.cancels += stats.cancels;
        totals.savesAndDeflects += stats.savesAndDeflects;
        return {
          reason,
          ...stats,
          deflectRate: stats.visits > 0 ? (stats.savesAndDeflects / stats.visits) * 100 : 0,
        };
      })
      .sort((a, b) => b.cancels - a.cancels);
    return { rows, totals: { ...totals, deflectRate: totals.visits > 0 ? (totals.savesAndDeflects / totals.visits) * 100 : 0 } };
  }, [peopleData?.data]);

  const reasonDetailData = useMemo(() => {
    if (!selectedReason || !peopleData?.data) return [];
    return peopleData.data.filter((person) => {
      const label = person.cancel_survey_response?.cancellation_reason?.label ?? "No reason given";
      return label === selectedReason;
    });
  }, [selectedReason, peopleData?.data]);

  const DETAIL_PAGE_SIZE = 20;
  const detailPageCount = Math.ceil(offerDetails.length / DETAIL_PAGE_SIZE);
  const pagedDetails = offerDetails.slice(
    (detailPage - 1) * DETAIL_PAGE_SIZE,
    detailPage * DETAIL_PAGE_SIZE
  );

  const handleSelectOffer = (offer_type: string, offer_value: string) => {
    setSelectedOffer({ offer_type, offer_value });
    setDetailPage(1);
  };

  const reasonDetailPageCount = Math.ceil(reasonDetailData.length / DETAIL_PAGE_SIZE);
  const pagedReasonDetails = reasonDetailData.slice(
    (reasonDetailPage - 1) * DETAIL_PAGE_SIZE,
    reasonDetailPage * DETAIL_PAGE_SIZE
  );

  const handleSelectReason = (reason: string) => {
    setSelectedReason(reason);
    setReasonDetailPage(1);
  };

  const exportReasonDetailsCsv = () => {
    if (reasonDetailData.length === 0) return;
    const headers = ["Date", "Customer", "Cancel ID", "Email", "Value", "Return Likelihood", "Competitor", "Comments"];
    const rows = reasonDetailData.map((person) => [
      new Date(person.started_at).toLocaleDateString(),
      person.user ? [person.user.first_name, person.user.last_name].filter(Boolean).join(" ") || person.user.email : "",
      person.uuid,
      person.user?.email ?? "",
      person.subscription_mrr ?? "",
      person.cancel_survey_response?.return_likelihood ?? "",
      person.cancel_survey_response?.competitor ?? "",
      person.cancel_survey_response?.free_text_comment ?? "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reason_detail_${selectedReason?.replace(/\s+/g, "_")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportOfferDetailsCsv = () => {
    if (offerDetails.length === 0) return;
    const headers = ["View Date", "Accept Date", "Offer", "Customer", "Email", "Accepted", "Outcome", "Reason", "MRR"];
    const rows = offerDetails.map(({ person, interaction }) => [
      interaction.shown_at ? new Date(interaction.shown_at).toLocaleString() : "",
      interaction.accepted_at ? new Date(interaction.accepted_at).toLocaleString() : "",
      formatOfferName(interaction.offer_type, interaction.offer_value),
      person.user ? [person.user.first_name, person.user.last_name].filter(Boolean).join(" ") : "",
      person.user?.email ?? "",
      interaction.accepted ? "Yes" : "No",
      person.outcome ?? "",
      person.cancel_survey_response?.cancellation_reason?.label ?? "",
      person.subscription_mrr ?? "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `offer_detail_${selectedOffer!.offer_type}_${selectedOffer!.offer_value}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Query strings carrying the active report filters to the detail screens,
  // so they open showing the same data. Param names differ per destination:
  // Cancel Sessions uses date_start/date_end, People uses date_from/date_to.
  const sessionsSearch = (outcome?: string) => {
    const params = new URLSearchParams();
    if (brand) params.set("brand", brand);
    if (dateFrom) params.set("date_start", dateFrom.toISOString());
    if (dateTo) params.set("date_end", dateTo.toISOString());
    if (outcome) params.set("outcome", outcome);
    const qs = params.toString();
    return `/cancel_sessions${qs ? `?${qs}` : ""}`;
  };
  const peopleSearch = (tab: string) => {
    const params = new URLSearchParams({ tab });
    if (brand) params.set("brand", brand);
    if (dateFrom) params.set("date_from", dateFrom.toISOString());
    if (dateTo) params.set("date_to", dateTo.toISOString());
    return `/cancel_people?${params.toString()}`;
  };
  const metricRowProps = (to: string) => ({
    onClick: () => navigate(to),
    style: { cursor: "pointer" },
  });

  const isLoading = summaryLoading || reasonsLoading || offersLoading;

  const refetchAll = () => {
    refetchSummary();
    refetchReasons();
    refetchOffers();
  };

  const summary = summaryData?.data;
  const reasons = reasonsData?.data as unknown as ReasonBreakdownItem[] | undefined;
  const offers = offersData?.data;

  const totalReasons = reasons
    ? reasons.reduce((sum: number, r: ReasonBreakdownItem) => sum + r.count, 0)
    : 0;

  return (
    <Flex direction="column" gap="lg">
      <Group position="apart">
        <Title order={3}>Cancel Flow Reports</Title>
        <Group>
          <Select
            placeholder="All Brands"
            data={[{ value: "", label: "All Brands" }, ...selectBrands]}
            value={brand}
            onChange={setBrand}
            clearable
            sx={{ width: 180 }}
          />
          <DatePicker
            placeholder="From"
            value={dateFrom}
            onChange={setDateFrom}
            clearable
            sx={{ width: 160 }}
          />
          <DatePicker
            placeholder="To"
            value={dateTo}
            onChange={setDateTo}
            clearable
            sx={{ width: 160 }}
          />
          <ActionIcon variant="subtle" onClick={refetchAll} loading={isLoading}>
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>
      </Group>

      <Tabs value={activeTab} onTabChange={(val) => { setActiveTab(val); setSelectedReason(null); setSelectedOffer(null); }}>
        <Tabs.List>
          <Tabs.Tab value="cancels" icon={<IconFileAnalytics size={16} />}>
            Cancels
          </Tabs.Tab>
          <Tabs.Tab value="reasons" icon={<IconChartBar size={16} />}>
            Reasons
          </Tabs.Tab>
          <Tabs.Tab value="offers" icon={<IconPercentage size={16} />}>
            Offer Performance
          </Tabs.Tab>
        </Tabs.List>

        {/* Cancels Tab */}
        <Tabs.Panel value="cancels" pt="md">
          {summaryLoading ? (
            <Flex justify="center" p="xl">
              <Loader />
            </Flex>
          ) : summary ? (
            <Flex direction="column" gap="md">
              <Paper p="md" radius="md" withBorder>
                <Title order={5} mb="md">
                  Session Outcomes
                </Title>
                <Table highlightOnHover>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Count</th>
                      <th>Percentage</th>
                      <th style={{ width: "40%" }}>Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr {...metricRowProps(sessionsSearch())}>
                      <td>
                        <Text size="sm" fw={700}>
                          Total Sessions
                        </Text>
                      </td>
                      <td>
                        <Text size="sm" fw={700}>
                          {summary.total}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm">100%</Text>
                      </td>
                      <td>
                        <Progress value={100} color="blue" size="lg" />
                      </td>
                    </tr>
                    <tr {...metricRowProps(peopleSearch("saves"))}>
                      <td>
                        <Badge color="green" variant="filled">
                          Deflected
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm" fw={700}>
                          {summary.deflected}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm" color="green">
                          {summary.total > 0
                            ? (
                                (summary.deflected / summary.total) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </Text>
                      </td>
                      <td>
                        <Progress
                          value={
                            summary.total > 0
                              ? (summary.deflected / summary.total) * 100
                              : 0
                          }
                          color="green"
                          size="lg"
                        />
                      </td>
                    </tr>
                    <tr {...metricRowProps(peopleSearch("cancels"))}>
                      <td>
                        <Badge color="red" variant="filled">
                          Cancelled
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm" fw={700}>
                          {summary.cancelled}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm" color="red">
                          {summary.total > 0
                            ? (
                                (summary.cancelled / summary.total) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </Text>
                      </td>
                      <td>
                        <Progress
                          value={
                            summary.total > 0
                              ? (summary.cancelled / summary.total) * 100
                              : 0
                          }
                          color="red"
                          size="lg"
                        />
                      </td>
                    </tr>
                    <tr {...metricRowProps(sessionsSearch("abandoned"))}>
                      <td>
                        <Badge color="gray" variant="filled">
                          Abandoned
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm" fw={700}>
                          {summary.abandoned}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm" color="dimmed">
                          {summary.total > 0
                            ? (
                                (summary.abandoned / summary.total) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </Text>
                      </td>
                      <td>
                        <Progress
                          value={
                            summary.total > 0
                              ? (summary.abandoned / summary.total) * 100
                              : 0
                          }
                          color="gray"
                          size="lg"
                        />
                      </td>
                    </tr>
                    <tr {...metricRowProps(sessionsSearch("in_progress"))}>
                      <td>
                        <Badge color="blue" variant="light">
                          In Progress
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm" fw={700}>
                          {summary.in_progress}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm" color="blue">
                          {summary.total > 0
                            ? (
                                (summary.in_progress / summary.total) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </Text>
                      </td>
                      <td>
                        <Progress
                          value={
                            summary.total > 0
                              ? (summary.in_progress / summary.total) * 100
                              : 0
                          }
                          color="blue"
                          size="lg"
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Paper>

              <Grid>
                <Grid.Col span={4}>
                  <Paper p="md" radius="md" withBorder>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Deflection Rate
                    </Text>
                    <Text size="xl" fw={700} color="green" mt={4}>
                      {summary.deflection_rate}%
                    </Text>
                    <Text size="xs" color="dimmed" mt={4}>
                      {summary.deflected} out of {summary.total} sessions
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Paper p="md" radius="md" withBorder>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Cancel Rate
                    </Text>
                    <Text size="xl" fw={700} color="red" mt={4}>
                      {summary.total > 0
                        ? (
                            (summary.cancelled / summary.total) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Text>
                    <Text size="xs" color="dimmed" mt={4}>
                      {summary.cancelled} out of {summary.total} sessions
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Paper p="md" radius="md" withBorder>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Abandon Rate
                    </Text>
                    <Text size="xl" fw={700} color="dimmed" mt={4}>
                      {summary.total > 0
                        ? (
                            (summary.abandoned / summary.total) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Text>
                    <Text size="xs" color="dimmed" mt={4}>
                      {summary.abandoned} out of {summary.total} sessions
                    </Text>
                  </Paper>
                </Grid.Col>
              </Grid>
            </Flex>
          ) : (
            <Text color="dimmed">No data available</Text>
          )}
        </Tabs.Panel>

        {/* Reasons Tab */}
        <Tabs.Panel value="reasons" pt="md">
          {peopleLoading && activeTab === "reasons" ? (
            <Flex justify="center" p="xl">
              <Loader />
            </Flex>
          ) : reasonStats.rows.length > 0 ? (
            <Flex direction="column" gap="md">
              {/* Donut chart + cancelled count */}
              <Paper p="md" radius="md" withBorder>
                <Flex align="center" gap={40}>
                  {(() => {
                    const size = 200;
                    const outerR = 90;
                    const innerR = 55;
                    const pieGen = d3.pie<(typeof reasonStats.rows)[number]>().value((d) => d.cancels).sort(null);
                    const arcGen = d3.arc<d3.PieArcDatum<(typeof reasonStats.rows)[number]>>().innerRadius(innerR).outerRadius(outerR);
                    const hoverArcGen = d3.arc<d3.PieArcDatum<(typeof reasonStats.rows)[number]>>().innerRadius(innerR).outerRadius(outerR + 6);
                    const labelArcGen = d3.arc<d3.PieArcDatum<(typeof reasonStats.rows)[number]>>().innerRadius((innerR + outerR) / 2).outerRadius((innerR + outerR) / 2);
                    const arcs = pieGen(reasonStats.rows);
                    return (
                      <svg width={size + 12} height={size + 12} style={{ overflow: "visible" }}>
                        <g transform={`translate(${(size + 12) / 2}, ${(size + 12) / 2})`}>
                          {arcs.map((a, i) => (
                            <path
                              key={a.data.reason}
                              d={(hoveredReasonIndex === i ? hoverArcGen : arcGen)(a) || ""}
                              fill={REASON_COLORS[i % REASON_COLORS.length]}
                              stroke="#fff"
                              strokeWidth={2}
                              style={{ cursor: "pointer", transition: "d 0.2s ease" }}
                              onClick={() => handleSelectReason(a.data.reason)}
                              onMouseEnter={() => setHoveredReasonIndex(i)}
                              onMouseLeave={() => setHoveredReasonIndex(null)}
                            />
                          ))}
                          {hoveredReasonIndex !== null && (() => {
                            const a = arcs[hoveredReasonIndex];
                            const pct = reasonStats.totals.cancels > 0
                              ? ((a.data.cancels / reasonStats.totals.cancels) * 100).toFixed(0)
                              : "0";
                            const [cx, cy] = labelArcGen.centroid(a);
                            return (
                              <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize={13}
                                fontWeight={700}
                                fill="white"
                                style={{ pointerEvents: "none", textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
                              >
                                {pct}%
                              </text>
                            );
                          })()}
                        </g>
                      </svg>
                    );
                  })()}
                  <Flex direction="column">
                    <Text size={36} fw={700} color="red">{summary?.cancelled ?? reasonStats.totals.cancels}</Text>
                    <Text size="sm" color="dimmed">Cancelled</Text>
                  </Flex>
                </Flex>
              </Paper>

              {/* Enhanced breakdown table */}
              <Paper p="md" radius="md" withBorder>
                <Title order={5} mb={4}>Breakdown</Title>
                <Text size="sm" color="dimmed" mb="md">Click a reason to view details</Text>
                <Table>
                  <thead>
                    <tr>
                      <th>Reason name</th>
                      <th style={{ textAlign: "right" }}>Visits</th>
                      <th style={{ width: "20%" }}>Cancels</th>
                      <th style={{ width: "15%" }}>Saves + deflects</th>
                      <th style={{ textAlign: "right" }}>Deflect rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reasonStats.rows.map((row, i) => {
                      const cancelPct = reasonStats.totals.cancels > 0
                        ? (row.cancels / reasonStats.totals.cancels) * 100
                        : 0;
                      return (
                        <tr
                          key={row.reason}
                          onClick={() => handleSelectReason(row.reason)}
                          onMouseEnter={() => setHoveredReasonIndex(i)}
                          onMouseLeave={() => setHoveredReasonIndex(null)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selectedReason === row.reason
                              ? "#e7f5ff"
                              : hoveredReasonIndex === i
                                ? "#f1f3f5"
                                : undefined,
                            transition: "background-color 0.15s ease",
                          }}
                        >
                          <td>
                            <Group spacing="xs">
                              <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: REASON_COLORS[i % REASON_COLORS.length] }} />
                              <Text size="sm">{row.reason}</Text>
                            </Group>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Text size="sm">{row.visits.toLocaleString()}</Text>
                          </td>
                          <td>
                            <Group spacing="xs" noWrap>
                              <Progress value={cancelPct} color="red" size="lg" sx={{ flex: 1 }} />
                              <Text size="sm" sx={{ whiteSpace: "nowrap" }}>
                                {cancelPct.toFixed(0)}% {row.cancels.toLocaleString()}
                              </Text>
                            </Group>
                          </td>
                          <td>
                            <Group spacing="xs" noWrap>
                              <Progress
                                value={row.visits > 0 ? (row.savesAndDeflects / row.visits) * 100 : 0}
                                color="green"
                                size="lg"
                                sx={{ flex: 1 }}
                              />
                              <Text size="sm">{row.savesAndDeflects.toLocaleString()}</Text>
                            </Group>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Text size="sm">{row.deflectRate.toFixed(1)}%</Text>
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: "2px solid #dee2e6" }}>
                      <td><Text size="sm" fw={700}>Total</Text></td>
                      <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{reasonStats.totals.visits.toLocaleString()}</Text></td>
                      <td>
                        <Text size="sm" fw={700}>{reasonStats.totals.cancels.toLocaleString()}</Text>
                      </td>
                      <td>
                        <Text size="sm" fw={700}>{reasonStats.totals.savesAndDeflects.toLocaleString()}</Text>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Text size="sm" fw={700}>{reasonStats.totals.deflectRate.toFixed(1)}%</Text>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Paper>

              {/* Drill-down detail panel */}
              {selectedReason && (
                <Paper p="md" radius="md" withBorder>
                  <Group position="apart" mb="md">
                    <Group spacing="lg">
                      <Title order={5}>
                        Cancels — Reason: {selectedReason}
                      </Title>
                      <Text size="sm" color="dimmed">
                        {reasonDetailData.length} result{reasonDetailData.length !== 1 ? "s" : ""}
                      </Text>
                    </Group>
                    <Group spacing="xs">
                      <Button
                        variant="subtle"
                        compact
                        leftIcon={<IconDownload size={16} />}
                        onClick={exportReasonDetailsCsv}
                        disabled={reasonDetailData.length === 0}
                      >
                        Export CSV
                      </Button>
                      <CloseButton onClick={() => setSelectedReason(null)} />
                    </Group>
                  </Group>

                  {pagedReasonDetails.length > 0 ? (
                    <>
                      <ScrollArea>
                        <Table highlightOnHover>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Customer</th>
                              <th>Cancel ID</th>
                              <th>Email</th>
                              <th style={{ textAlign: "right" }}>Value</th>
                              <th style={{ textAlign: "right" }}>Return Likelihood</th>
                              <th>Competitor</th>
                              <th>Comments</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedReasonDetails.map((person) => (
                              <tr key={person.id}>
                                <td>
                                  <Text size="sm">{new Date(person.started_at).toLocaleDateString()}</Text>
                                </td>
                                <td>
                                  <Text size="sm">
                                    {person.user
                                      ? [person.user.first_name, person.user.last_name].filter(Boolean).join(" ") || person.user.email
                                      : "—"}
                                  </Text>
                                </td>
                                <td>
                                  <Text size="xs" color="dimmed" sx={{ fontFamily: "monospace" }}>
                                    {person.uuid.slice(0, 8)}
                                  </Text>
                                </td>
                                <td>
                                  {person.user ? (
                                    <Anchor component={Link} to={`/users/show/${person.user.id}`} size="sm">
                                      {person.user.email}
                                    </Anchor>
                                  ) : (
                                    <Text color="dimmed">—</Text>
                                  )}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  <Text size="sm">
                                    {person.subscription_mrr ? `$${person.subscription_mrr}` : "—"}
                                  </Text>
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  {(() => {
                                    const value = person.cancel_survey_response?.return_likelihood;
                                    if (value == null) return <Text color="dimmed">—</Text>;
                                    const color = value >= 7 ? "green" : value >= 4 ? "yellow" : "red";
                                    return <Badge variant="filled" color={color}>{value}/10</Badge>;
                                  })()}
                                </td>
                                <td>
                                  <Text size="sm">
                                    {person.cancel_survey_response?.competitor ?? "—"}
                                  </Text>
                                </td>
                                <td>
                                  <Text size="sm" lineClamp={2} sx={{ maxWidth: 250 }}>
                                    {person.cancel_survey_response?.free_text_comment ?? "—"}
                                  </Text>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </ScrollArea>
                      {reasonDetailPageCount > 1 && (
                        <Group position="right" mt="md">
                          <Pagination
                            total={reasonDetailPageCount}
                            page={reasonDetailPage}
                            onChange={setReasonDetailPage}
                          />
                        </Group>
                      )}
                    </>
                  ) : (
                    <Text color="dimmed" py="md">No records found for this reason</Text>
                  )}
                </Paper>
              )}
            </Flex>
          ) : (
            <Text color="dimmed">No cancel reasons data</Text>
          )}
        </Tabs.Panel>

        {/* Offers Tab */}
        <Tabs.Panel value="offers" pt="md">
          {offersLoading ? (
            <Flex justify="center" p="xl">
              <Loader />
            </Flex>
          ) : offers ? (
            <Flex direction="column" gap="md">
              <Grid>
                <Grid.Col span={4}>
                  <Paper p="md" radius="md" withBorder>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Total Offers Shown
                    </Text>
                    <Text size="xl" fw={700} mt={4}>
                      {offers.total_shown}
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Paper p="md" radius="md" withBorder>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Total Accepted
                    </Text>
                    <Text size="xl" fw={700} color="green" mt={4}>
                      {offers.total_accepted}
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Paper p="md" radius="md" withBorder>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Acceptance Rate
                    </Text>
                    <Text size="xl" fw={700} color="teal" mt={4}>
                      {offers.acceptance_rate}%
                    </Text>
                  </Paper>
                </Grid.Col>
              </Grid>

              {offers.by_offer && offers.by_offer.length > 0 && (() => {
                const totals = offers.by_offer.reduce(
                  (acc, item) => ({
                    shown: acc.shown + item.shown,
                    accepted: acc.accepted + item.accepted,
                    saved: acc.saved + item.saved,
                    lost: acc.lost + item.lost,
                    watching: acc.watching + item.watching,
                    saved_revenue: acc.saved_revenue + item.saved_revenue,
                  }),
                  { shown: 0, accepted: 0, saved: 0, lost: 0, watching: 0, saved_revenue: 0 }
                );
                const totalAcceptRate = totals.shown > 0 ? (totals.accepted / totals.shown) * 100 : 0;
                const totalSaveRate = totals.accepted > 0 ? (totals.saved / totals.accepted) * 100 : 0;

                return (
                  <Paper p="md" radius="md" withBorder>
                    <Title order={5} mb={4}>Breakdown</Title>
                    <Text size="sm" color="dimmed" mb="md">
                      View highest performing offers
                    </Text>
                    <Table>
                      <thead>
                        <tr>
                          <th>Offer name</th>
                          <th style={{ textAlign: "right" }}>Views</th>
                          <th style={{ textAlign: "right" }}>Accepted</th>
                          <th style={{ textAlign: "right" }}>Accept Rate</th>
                          <th style={{ textAlign: "right" }}>Saved Revenue</th>
                          <th style={{ textAlign: "right" }}>Lost Accepts</th>
                          <th style={{ textAlign: "right" }}>Watchlist Accepts</th>
                          <th style={{ textAlign: "right" }}>Save Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offers.by_offer.map((item) => (
                          <tr
                            key={`${item.offer_type}-${item.offer_value}`}
                            onClick={() => handleSelectOffer(item.offer_type, item.offer_value)}
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                selectedOffer?.offer_type === item.offer_type &&
                                selectedOffer?.offer_value === item.offer_value
                                  ? "#e7f5ff"
                                  : undefined,
                            }}
                          >
                            <td>
                              <Text size="sm">{formatOfferName(item.offer_type, item.offer_value)}</Text>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <Text size="sm">{item.shown.toLocaleString()}</Text>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <Text size="sm">{item.accepted.toLocaleString()}</Text>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <Text size="sm">{item.acceptance_rate.toFixed(1)}%</Text>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <Text size="sm">${item.saved_revenue.toLocaleString()}</Text>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <Text size="sm">{item.lost.toLocaleString()}</Text>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <Text size="sm">{item.watching.toLocaleString()}</Text>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <Text size="sm">{item.save_rate.toFixed(1)}%</Text>
                            </td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: "2px solid #dee2e6" }}>
                          <td><Text size="sm" fw={700}>Total</Text></td>
                          <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{totals.shown.toLocaleString()}</Text></td>
                          <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{totals.accepted.toLocaleString()}</Text></td>
                          <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{totalAcceptRate.toFixed(1)}%</Text></td>
                          <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>${totals.saved_revenue.toLocaleString()}</Text></td>
                          <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{totals.lost.toLocaleString()}</Text></td>
                          <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{totals.watching.toLocaleString()}</Text></td>
                          <td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{totalSaveRate.toFixed(1)}%</Text></td>
                        </tr>
                      </tbody>
                    </Table>
                  </Paper>
                );
              })()}

              {selectedOffer && (() => {
                const selectedItem = offers.by_offer?.find(
                  (i) =>
                    i.offer_type === selectedOffer.offer_type &&
                    i.offer_value === selectedOffer.offer_value
                );
                return (
                  <Paper p="md" radius="md" withBorder>
                    <Group position="apart" mb="md">
                      <Group spacing="lg">
                        <Title order={5}>
                          Offer detail — {formatOfferName(selectedOffer.offer_type, selectedOffer.offer_value)}
                        </Title>
                        {selectedItem && (
                          <Group spacing="md">
                            <Text size="sm" color="dimmed">
                              Views: <Text component="span" fw={700}>{selectedItem.shown.toLocaleString()}</Text>
                            </Text>
                            <Text size="sm" color="dimmed">
                              Accepted: <Text component="span" fw={700}>{selectedItem.accepted.toLocaleString()}</Text>
                            </Text>
                            <Text size="sm" color="dimmed">
                              Rate: <Text component="span" fw={700}>{selectedItem.acceptance_rate.toFixed(1)}%</Text>
                            </Text>
                          </Group>
                        )}
                      </Group>
                      <Group spacing="xs">
                        <Button
                          variant="subtle"
                          compact
                          leftIcon={<IconDownload size={16} />}
                          onClick={exportOfferDetailsCsv}
                          disabled={offerDetails.length === 0}
                        >
                          Export
                        </Button>
                        <CloseButton onClick={() => setSelectedOffer(null)} />
                      </Group>
                    </Group>

                    {peopleLoading ? (
                      <Flex justify="center" p="xl">
                        <Loader />
                      </Flex>
                    ) : offerDetails.length > 0 ? (
                      <>
                        <ScrollArea>
                          <Table highlightOnHover>
                            <thead>
                              <tr>
                                <th>View Date</th>
                                <th>Accept Date</th>
                                <th>Offer</th>
                                <th>Customer</th>
                                <th>Accepted</th>
                                <th>Outcome</th>
                                <th>Email</th>
                                <th>Reason</th>
                                <th style={{ textAlign: "right" }}>MRR</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedDetails.map(({ person, interaction }) => (
                                <tr key={`${person.id}-${interaction.id}`}>
                                  <td>
                                    <Text size="sm">
                                      {interaction.shown_at
                                        ? new Date(interaction.shown_at).toLocaleDateString()
                                        : "—"}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="sm">
                                      {interaction.accepted_at
                                        ? new Date(interaction.accepted_at).toLocaleDateString()
                                        : "—"}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="sm">
                                      {formatOfferName(interaction.offer_type, interaction.offer_value)}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="sm">
                                      {person.user
                                        ? [person.user.first_name, person.user.last_name].filter(Boolean).join(" ") || person.user.email
                                        : "—"}
                                    </Text>
                                  </td>
                                  <td>
                                    <Badge color={interaction.accepted ? "green" : "gray"}>
                                      {interaction.accepted ? "Yes" : "No"}
                                    </Badge>
                                  </td>
                                  <td>
                                    {person.outcome ? (
                                      <Badge color={colorsByOutcome[person.outcome]}>
                                        {person.outcome}
                                      </Badge>
                                    ) : (
                                      <Badge color="gray">Pending</Badge>
                                    )}
                                  </td>
                                  <td>
                                    <Text size="sm">{person.user?.email ?? "—"}</Text>
                                  </td>
                                  <td>
                                    <Text size="sm">
                                      {person.cancel_survey_response?.cancellation_reason?.label ?? "—"}
                                    </Text>
                                  </td>
                                  <td style={{ textAlign: "right" }}>
                                    <Text size="sm">
                                      {person.subscription_mrr ? `$${person.subscription_mrr}` : "—"}
                                    </Text>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </ScrollArea>
                        <Group position="apart" mt="md">
                          <Text size="sm" color="dimmed">
                            Showing {offerDetails.length} result{offerDetails.length !== 1 ? "s" : ""}
                          </Text>
                          {detailPageCount > 1 && (
                            <Pagination
                              total={detailPageCount}
                              page={detailPage}
                              onChange={setDetailPage}
                            />
                          )}
                        </Group>
                      </>
                    ) : (
                      <Text color="dimmed" py="md">No individual records found for this offer</Text>
                    )}
                  </Paper>
                );
              })()}
            </Flex>
          ) : (
            <Text color="dimmed">No offers data</Text>
          )}
        </Tabs.Panel>
      </Tabs>
    </Flex>
  );
};
